/**
 * Store do chat do Especialista em Comissionamento (aba de IA do módulo).
 *
 * Namespace PRÓPRIO, separado do `aawz-intelligence-v2`: `selectedConversation` e
 * `messages` moram no store, então compartilhar o namespace faria uma pergunta
 * feita aqui ser anexada à conversa que o usuário tem aberta no chat do CRM — e
 * com a persona errada no histórico.
 *
 * ★ Os nomes das actions e das mutations são CONTRATO do `APIChat` (ver
 * `@modules/aawz-intelligence-v2/api/apiChat`): é ele quem despacha, por string
 * montada com o namespace. Renomear qualquer um destes não quebra o build nem
 * acende lint — o Vuex só avisa no console e o streaming para de atualizar a tela
 * em silêncio.
 *
 * O que este store NÃO tem, de propósito: os canais do painel deliberativo
 * (`servicosCitados*`, `painel*`, `faseDoTurno`) e `persona` do toggle. O
 * `apiChat` despacha esses no namespace FIXO do módulo principal, e a persona
 * desta superfície é constante — vem do endpoint, não da escolha do usuário.
 *
 * O que ele TEM por composição, como o `feeFixoIa` e o `aiFramework`: o slice
 * `toolParts` (as partes de tool do turno — é nele que o cartão de confirmação
 * `crm/confirmacao` das write-tools da Fase 3 mora, spec-010) e o slice
 * `hub-e-agente` (badges do turno). Os dois são despachados pelo motor no
 * namespace de QUEM o instanciou; sem a composição, cada turno levantava
 * `[vuex] unknown action type: comissoesIa/upsertToolPart` e o cartão nunca
 * aparecia. Quem trava a junção é `superficies-do-chat.unit.js`.
 */
import { PERSONA_ESPECIALISTA_COMISSOES } from '@modules/aawz-intelligence-v2/constants/Personas'
import {
  criarStateToolParts,
  toolPartsActions,
  toolPartsGetters,
  toolPartsMutations,
} from '@modules/aawz-intelligence-v2/store/toolParts'
import {
  criarStateHubEAgente,
  hubEAgenteActions,
  hubEAgenteGetters,
  hubEAgenteMutations,
} from '@modules/aawz-intelligence-v2/store/hub-e-agente'

export const state = () => ({
  messages: [],
  chatLoading: false,
  responseTokens: [],
  thinkingSteps: [],
  toolCalls: [],
  // O `APIChat.resetChatState` despacha `resetPendingConfirmation` em TODO envio.
  // Aqui ele nunca sai de `null`: a proposta de escrita da Fase 3 é uma PARTE
  // do turno no slice `toolParts` (`approval-requested`), não este campo legado
  // — mas a action precisa existir.
  pendingConfirmation: null,
  selectedConversation: null,
  // Estado PRÓPRIO deste namespace (funções, nunca objeto compartilhado): um
  // mapa compartilhado faria a aba mostrar os cartões do chat do CRM.
  ...criarStateToolParts(),
  ...criarStateHubEAgente(),
})

export const getters = {
  ...toolPartsGetters,
  ...hubEAgenteGetters,
  messages: (state) => state.messages || [],
  chatLoading: (state) => !!state.chatLoading,
  // Array, nunca string: o `messages` repassa o getter inteiro como
  // `streaming-tokens` ao `chat-message.vue`, cuja prop é `Array` e é quem faz o
  // `join('')`. Mesmo shape do getter homônimo do `aawz-intelligence-v2`.
  responseTokens: (state) => state.responseTokens || [],
  thinkingSteps: (state) => state.thinkingSteps || [],
  toolCalls: (state) => state.toolCalls || [],
  pendingConfirmation: (state) => state.pendingConfirmation,
  selectedConversation: (state) => state.selectedConversation,
  conversationId: (state) =>
    state.selectedConversation && state.selectedConversation.id
      ? state.selectedConversation.id
      : null,
  // Constante: quem fixa a persona é a rota dedicada. O `messages` lê este getter
  // para resolver o rótulo da bolha e as flags de render — sem ele a tela cairia
  // na default (IA Operacional) e passaria a renderizar HTML.
  persona: () => PERSONA_ESPECIALISTA_COMISSOES,
}

export const mutations = {
  ...toolPartsMutations,
  ...hubEAgenteMutations,
  SET_MESSAGES: (state, payload) => {
    state.messages = payload || []
  },
  ADD_MESSAGE: (state, payload) => {
    state.messages = [...state.messages, payload]
  },
  SET_CHAT_LOADING: (state, payload) => {
    state.chatLoading = !!payload
  },
  ADD_RESPONSE_TOKENS: (state, payload) => {
    state.responseTokens = [...state.responseTokens, ...(payload || [])]
  },
  RESET_RESPONSE_TOKENS: (state) => {
    state.responseTokens = []
  },
  ADD_THINKING_STEP: (state, payload) => {
    state.thinkingSteps = [...state.thinkingSteps, payload]
  },
  RESET_THINKING_STEPS: (state) => {
    state.thinkingSteps = []
  },
  ADD_TOOL_CALL: (state, payload) => {
    state.toolCalls = [...state.toolCalls, payload]
  },
  // Remove UMA ocorrência: a mesma tool pode estar em voo duas vezes no turno, e
  // filtrar por valor apagaria o badge da que ainda está rodando.
  REMOVE_TOOL_CALL: (state, toolName) => {
    const idx = state.toolCalls.indexOf(toolName)
    if (idx === -1) return
    state.toolCalls = state.toolCalls.filter((_, i) => i !== idx)
  },
  RESET_TOOL_CALLS: (state) => {
    state.toolCalls = []
  },
  SET_PENDING_CONFIRMATION: (state, payload) => {
    state.pendingConfirmation = payload
  },
  RESET_PENDING_CONFIRMATION: (state) => {
    state.pendingConfirmation = null
  },
  SET_SELECTED_CONVERSATION: (state, payload) => {
    state.selectedConversation = payload
  },
}

export const actions = {
  ...toolPartsActions,
  ...hubEAgenteActions,
  addMessage: (context, payload) => context.commit('ADD_MESSAGE', payload),
  setChatLoading: (context, payload) =>
    context.commit('SET_CHAT_LOADING', payload),
  addResponseTokens: (context, payload) =>
    context.commit('ADD_RESPONSE_TOKENS', payload),
  resetResponseTokens: (context) => context.commit('RESET_RESPONSE_TOKENS'),
  addThinkingStep: (context, payload) =>
    context.commit('ADD_THINKING_STEP', payload),
  resetThinkingSteps: (context) => context.commit('RESET_THINKING_STEPS'),
  addToolCall: (context, payload) => context.commit('ADD_TOOL_CALL', payload),
  removeToolCall: (context, payload) =>
    context.commit('REMOVE_TOOL_CALL', payload),
  resetToolCalls: (context) => context.commit('RESET_TOOL_CALLS'),
  setPendingConfirmation: (context, payload) =>
    context.commit('SET_PENDING_CONFIRMATION', payload),
  resetPendingConfirmation: (context) =>
    context.commit('RESET_PENDING_CONFIRMATION'),
  setSelectedConversation: (context, payload) =>
    context.commit('SET_SELECTED_CONVERSATION', payload),
  // Zera a conversa inteira — é o "Nova conversa" da tela.
  limparConversa: (context) => {
    context.commit('SET_MESSAGES', [])
    context.commit('SET_SELECTED_CONVERSATION', null)
    context.commit('RESET_RESPONSE_TOKENS')
    context.commit('RESET_THINKING_STEPS')
    context.commit('RESET_TOOL_CALLS')
    context.commit('RESET_PENDING_CONFIRMATION')
    // As partes de tool NÃO são zeradas aqui, como nas outras superfícies: com
    // `messages` vazio nada as desenha, e o motor as limpa no início de todo
    // turno (`resetChatState` → `clearToolParts`).
  },
}
